import React, { useEffect, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { api } from '../../lib/api';
import { SectionCard } from '../Common';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';

/**
 * Datos de la clínica que el código ya no trae escritos (V2, 28/9/2026).
 *
 * La cuenta de los pagos, el enlace de pago de la reserva de quirófano, las
 * fichas de Google de las reseñas, los datos de las recetas y los textos de
 * WhatsApp con lo del local viven en Configuración y en ningún otro sitio.
 * Cada campo tiene `id="ajuste-<campo>"` para que la lista «Datos de la
 * clínica que faltan» lleve a él. Solo dirección los edita (el servidor lo
 * exige igual) y cada cambio queda en el rastro con su valor de antes.
 */

/** Lleva al campo y lo enfoca: los enlaces de la lista de lo que falta. */
export function irAlCampo(campo) {
  const el = document.getElementById(`ajuste-${campo}`);
  if (!el) return;
  if (typeof el.scrollIntoView === 'function') el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  const foco = el.matches('input, textarea') ? el : el.querySelector('input, textarea, button');
  if (foco && typeof foco.focus === 'function') foco.focus({ preventScroll: true });
}

export function FaltanDatosClinica({ faltan }) {
  if (!faltan || faltan.length === 0) return null;
  const obligatorios = faltan.filter((f) => !f.opcional);
  const opcionales = faltan.filter((f) => f.opcional);
  const fila = (f) => (
    <li key={f.campo} className="text-sm">
      <a
        href={`#ajuste-${f.campo}`}
        onClick={(e) => { e.preventDefault(); irAlCampo(f.campo); }}
        className="font-medium text-[#13231E] underline underline-offset-2 hover:text-[#2E6E5E]"
        data-testid={`settings-falta-${f.campo}`}
      >
        {f.etiqueta}
      </a>
      {f.opcional && <span className="text-muted-foreground"> (opcional)</span>}
      <span className="text-muted-foreground"> — {f.efecto}</span>
    </li>
  );
  return (
    <div className="cjo-card p-5 mb-6 border border-amber-200 bg-amber-50/60" data-testid="settings-faltan">
      <div className="flex items-center gap-2 mb-2">
        <AlertTriangle className="w-4 h-4 text-amber-700" />
        <h3 className="font-display font-semibold text-[#13231E] text-base">Datos de la clínica que faltan</h3>
      </div>
      <p className="text-sm text-muted-foreground mb-3">
        Mientras falten, lo que los usa sale sin ellos (sin romperse). Pulsa cada uno para ir a su campo.
      </p>
      {obligatorios.length > 0 && <ul className="space-y-1.5 list-disc pl-5">{obligatorios.map(fila)}</ul>}
      {opcionales.length > 0 && <ul className="space-y-1.5 list-disc pl-5 mt-2">{opcionales.map(fila)}</ul>}
    </div>
  );
}

function Campo({ campo, label, form, setF, soloLectura, placeholder = '', ayuda = '', className = '' }) {
  return (
    <div className={className}>
      <Label htmlFor={`ajuste-${campo}`}>{label}</Label>
      <Input
        id={`ajuste-${campo}`}
        disabled={soloLectura}
        value={form[campo] || ''}
        placeholder={placeholder}
        onChange={(e) => setF(campo, e.target.value)}
        data-testid={`settings-${campo}`}
      />
      {ayuda && <p className="text-xs text-muted-foreground mt-1">{ayuda}</p>}
    </div>
  );
}

export function AjustesPagosResenas({ form, setF, soloLectura }) {
  const p = { form, setF, soloLectura };
  return (
    <SectionCard title="Pagos y reseñas">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Campo campo="pago_iban" label="IBAN de los pagos" className="md:col-span-2" {...p}
          ayuda="Se enseña en el paso de pago de la guía «mis pasos» y en la confirmación de un retoque con coste. Sin IBAN, no hay paso de pago." />
        <Campo campo="pago_bic" label="BIC" {...p} />
        <Campo campo="pago_destinatario" label="Titular de la cuenta" {...p} />
        <Campo campo="stripe_reserva_qx_url" label="Enlace de pago de la reserva de quirófano" className="md:col-span-2" {...p}
          placeholder="https://…"
          ayuda="Va en la confirmación de cirugía. Sin enlace, el mensaje no pide el pago de la reserva; tampoco a los pacientes con una marca de paciente que la paga fuera (Marcas de paciente)." />
        <Campo campo="resena_cjo_nombre" label="Reseñas · centro principal: nombre" {...p} />
        <Campo campo="resena_cjo_url" label="Reseñas · centro principal: enlace de Google" {...p} placeholder="https://…" />
        <Campo campo="resena_instituto_nombre" label="Reseñas · segundo centro: nombre" {...p} />
        <Campo campo="resena_instituto_url" label="Reseñas · segundo centro: enlace de Google" {...p} placeholder="https://…"
          ayuda="Déjalo vacío si solo hay un centro: sin enlace, ese centro no se ofrece al pedir reseña." />
      </div>
    </SectionCard>
  );
}

export function AjustesRecetas({ form, setF, soloLectura }) {
  const p = { form, setF, soloLectura };
  return (
    <SectionCard title="Recetas y justificantes">
      <p className="text-xs text-muted-foreground mb-3">
        Lo que la receta pone cuando el lote del colegio no lo trae, y el lugar de expedición de los justificantes.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Campo campo="doctor_specialty" label="Especialidad del doctor" {...p} />
        <Campo campo="colegio_medicos" label="Colegio de médicos" {...p} />
        <Campo campo="postal_code" label="Código postal" {...p} />
        <Campo campo="city" label="Municipio" {...p} />
        <Campo campo="province_code" label="Código de provincia" {...p} placeholder="p. ej. 28" />
      </div>
    </SectionCard>
  );
}

const PLANTILLAS = [
  ['wa_plantilla_confirmacion', 'Confirmación de cita presencial',
    'Se manda al dar la cita. Aquí va lo del local: cómo llegar, a pie de calle, parking…'],
  ['wa_plantilla_recordatorio', 'Recordatorio de cita presencial',
    'Se manda unos días antes. {{ENLACE_CONFIRMAR}} es el enlace para confirmar y {{DOCUMENTACION}} lo que le falte rellenar al paciente.'],
];

export function AjustesPlantillasWhatsApp({ form, setF, soloLectura }) {
  const [genericas, setGenericas] = useState({});
  const [marcadores, setMarcadores] = useState([]);
  useEffect(() => {
    let vivo = true;
    api.get('/api/settings/plantillas-whatsapp')
      .then(({ data }) => {
        if (!vivo) return;
        setGenericas(data?.genericas || {});
        setMarcadores(Array.isArray(data?.marcadores) ? data.marcadores : []);
      })
      .catch(() => {});
    return () => { vivo = false; };
  }, []);
  return (
    <SectionCard title="Mensajes de WhatsApp de la cita" className="lg:col-span-2">
      <p className="text-xs text-muted-foreground mb-3">
        Vacío = el texto genérico (el que se ve en gris), sin datos del local. La cita online tiene su propio texto.
        {' '}En las agendas con marca (Gestión de agendas), el nombre, la dirección y el teléfono salen de
        la marca (Configuración → Marcas) y el resto del texto es este mismo.
        {marcadores.length > 0 && (
          <> Marcadores: {marcadores.map((m) => `{{${m}}}`).join(' ')}</>
        )}
      </p>
      <div className="space-y-4">
        {PLANTILLAS.map(([campo, titulo, ayuda]) => (
          <div key={campo}>
            <div className="flex items-center justify-between gap-2">
              <Label htmlFor={`ajuste-${campo}`}>{titulo}</Label>
              {!soloLectura && genericas[campo] && !form[campo] && (
                <Button type="button" variant="outline" size="sm"
                  onClick={() => setF(campo, genericas[campo])}
                  data-testid={`settings-${campo}-partir`}>
                  Partir del texto genérico
                </Button>
              )}
            </div>
            <Textarea
              id={`ajuste-${campo}`}
              disabled={soloLectura}
              rows={9}
              className="font-mono text-xs mt-1"
              value={form[campo] || ''}
              placeholder={genericas[campo] || ''}
              onChange={(e) => setF(campo, e.target.value)}
              data-testid={`settings-${campo}`}
            />
            <p className="text-xs text-muted-foreground mt-1">{ayuda}</p>
          </div>
        ))}
      </div>
    </SectionCard>
  );
}
